import { MouseEvent } from "react"
import { GridColDef, GridActionsCellItem } from "@mui/x-data-grid-pro"
import { Box, Tooltip, Typography } from "@mui/material"
import { StudentAcademicStatusDTO } from "../studentAcademicStatus.dto"
import AppIcon from "@utils/appIcon"
import appIcons from "@utils/appIcons"
import { ColumnKeys } from "./columns/column.keys"
import { store } from "@app/store"
import { UsersClient } from "@clients/usersClient"
import { showErrorSnackbar, showSuccessSnackbar } from "@features/ui/uiSlice"
import { viewAsUser } from "../../../account-management/accountManagement"
import { buildStudentActions } from "../../../programs/program/students/studentActions"

// The grid row carries the program id but not the program, and the canonical
// action set gates legacy vs next-gen items. Programs are fetched app-wide, so
// resolve the type from the store rather than threading it through the DTO.
function resolveProgramType(
  programId: string | undefined,
): "legacy" | "next-gen" {
  if (!programId) return "next-gen"
  const program = store
    .getState()
    .program.programs.find((candidate) => candidate.id === programId)
  return program?.cohortType === "legacy" ? "legacy" : "next-gen"
}

// The one place that knows how a HubSpot contact id becomes a contact URL —
// the opt-in Hubspot ID column cell and the panel header must never diverge.
export function hubspotContactUrl(hubspotContactId: string): string {
  return `https://app.hubspot.com/contacts/9484219/record/0-1/${hubspotContactId}`
}

// Same tab-scoped impersonation as viewAsUser, plus a landing page: the campus
// login proxy already honours a redirectUrl search param (LoginProxyToken
// navigates there after the token exchange), so landing on /calendar rides the
// existing link — no new auth flow.
const viewAsCampusCalendar = async (email: string | undefined) => {
  if (!email) {
    return
  }
  try {
    const link = await UsersClient.generateImpersonationLink(email, "campus")
    const url = `${link}${
      link.includes("?") ? "&" : "?"
    }impersonate=1&redirectUrl=${encodeURIComponent("/calendar")}`
    window.open(url, "_blank", "noopener")
    store.dispatch(
      showSuccessSnackbar(
        "Opened Campus on their calendar in a new tab · your own session is untouched.",
      ),
    )
  } catch {
    store.dispatch(
      showErrorSnackbar("Failed to open impersonation, try again later."),
    )
  }
}

export function getActionsColumn(
  onManageProgramsClicked: (studentId: string, studentName: string) => void,
): GridColDef<StudentAcademicStatusDTO> {
  return {
    field: ColumnKeys.ACTIONS,
    headerName: "Actions",
    // One visible view-as button now (Campus only), so the column is narrower —
    // no dead width where the LMS button used to sit.
    width: 130,
    type: "actions",
    getActions: (params: { row: StudentAcademicStatusDTO }) => {
      const { name, email } = params.row.contactDetails
      const registration = params.row.registrationDetails
      const [firstName = "", ...restOfName] = (name ?? "").trim().split(" ")
      // THE canonical set — identical to the program-students kebab.
      const canonical = buildStudentActions({
        studentId: params.row.studentId,
        name,
        firstName,
        lastName: restOfName.join(" "),
        email,
        programId: registration?.programId,
        classId: registration?.classId,
        registrationClassId: registration?.classId,
        mentorId: params.row.staff.mentor?.userId,
        hasMentor: params.row.staff.mentor !== null,
        // The students DTO does not carry the CSA yet, so the popup opens
        // without a preselected advisor — it still assigns correctly.
        csaClientId: undefined,
        hasCsa: false,
        programType: resolveProgramType(registration?.programId),
      })
      // The grid has no divider entries: a divider is a prop on the item it
      // follows, so fold the registry's dividers onto their preceding item.
      const dividerAfter = new Set<string>()
      canonical.forEach((entry, index) => {
        if (entry.kind !== "divider") return
        const previous = canonical[index - 1]
        if (previous && previous.kind === "item") dividerAfter.add(previous.key)
      })
      return [
        // ALWAYS-VISIBLE Campus view-as (Mayer: invisible actions are a
        // discoverability bug): the eye is THE impersonation glyph app-wide.
        // Campus only — students have no LMS access, so a "View as LMS" would
        // open a surface they can't use (Mayer). The LMS copy-link is gone too.
        <GridActionsCellItem
          icon={
            <Tooltip title="Open Campus as this student · new tab, your session stays">
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <AppIcon
                  icon={appIcons.eye}
                  props={{ sx: { width: "16px", height: "16px" } }}
                />
                <Typography variant="body3">Campus</Typography>
              </Box>
            </Tooltip>
          }
          label="View as Campus"
          onClick={(event: MouseEvent) => {
            // The row click toggles the detail panel; a view-as must not.
            event.stopPropagation()
            viewAsUser(email)
          }}
          disabled={!email}
          sx={{ borderRadius: "6px", px: "6px" }}
        />,
        // Kebab items spread extra props onto their MenuItem, so a native
        // title carries the session-clarity line the menu row has no room
        // for: what impersonates says so, what doesn't says what it does.
        <GridActionsCellItem
          icon={
            <AppIcon
              icon={appIcons.eye}
              props={{ sx: { width: "16px", height: "16px" } }}
            />
          }
          onClick={() => viewAsCampusCalendar(email)}
          label="View as Campus · Calendar"
          title="Opens Campus on their calendar as this student · new tab, your session stays"
          showInMenu
          divider
          disabled={!email}
        />,
        <GridActionsCellItem
          icon={
            <AppIcon
              icon={appIcons.bookOpen01}
              props={{ sx: { width: "16px", height: "16px" } }}
            />
          }
          onClick={() => onManageProgramsClicked(params.row.studentId, name)}
          label="Manage programs"
          showInMenu
          divider
        />,
        // The canonical student actions (studentActions.ts) — the SAME list the
        // program-students kebab renders. This menu used to carry a hand-picked
        // handful, which is how registration, mentor/CSA, language, unlock and
        // offboard ended up unreachable from the page staff actually work in.
        ...canonical
          .filter(
            (action): action is Extract<typeof action, { kind: "item" }> =>
              action.kind === "item",
          )
          .map((action) => (
            <GridActionsCellItem
              key={action.key}
              label={action.label}
              onClick={action.onClick}
              showInMenu
              divider={dividerAfter.has(action.key)}
              sx={action.danger ? { color: "error.main" } : undefined}
            />
          )),
      ]
    },
  }
}
